import {Moment} from 'moment';
import {Theme} from 'stores/AppStore';
import {UserObject} from 'stores/UserStore';
import {FileObject} from 'api/library';

export interface ThemeObject {
  name: Theme;
  variables: {
    [name: string]: string;
  };
  colors: {
    [name: string]: string;
  };
}

export type ID = string;
export type Slug = string;

export enum Currency {
  RUB = 'rub',
  USD = 'usd',
}

export interface PriceObject {
  amount: number;
  currency: Currency;
}

export enum PreviewType {
  Image = 'image',
  Video = 'video',
  Audio = 'audio',
  External = 'external'
}

export type PreviewObject
  = Pick<FileObject, 'id' | 'type' | 'url'>;

export interface ErrorObject {
  error?: string;
  statusCode: number;
  message: string | string[];
}

export interface DeleteObject {
  message: string;
}

export interface RangeDateObject {
  startDate: Moment | string;
  overDate: Moment | string;
}

export enum PromocodeType {
  Percent = 'percent',
  Fixed = 'fixed',
}

export interface PromocodeObject {
  code: string;
  type: PromocodeType;
  discount: number;
  dates: Partial<RangeDateObject>;
  limits?: number;
}

export type ToggleProperty<T extends object = {}> = {
  active: boolean;
} & T;

export type Tag = string;
export type Folder = string;

/**
 * The interface of object with id.
 * @param {ID} id
 */
export interface IDObject {

  /**
   * Required uniq id.
   * @type {ID}
   */
  id: ID;

}

/**
 * The type of header object.
 * @param {string} title
 * @param {string} description
 * @param {PreviewObject | undefined} preview
 */
export type HeaderObject = Partial<AuthorObject> & {

  /**
   * Required title of the topic.
   * @param {string}
   */
  title: string;

  /**
   * Required description of the topic.
   * @param {string}
   */
  description: string;

  /**
   * Preview thumbnail of the topic.
   * @type {PreviewObject | undefined}
   */
  preview: PreviewObject | null | undefined;

}

/**
 * The interface of object with folders.
 * @param {Folder[] | undefined} folders
 */
export interface FoldersObject {

  /**
   * The array of folders.
   * @type {Folder[] | undefined}
   */
  folders?: Folder[];

}

/**
 * The interface of object with tags.
 * @param {Tag[] | undefined} tags
 */
export interface TagsObject {

  /**
   * The array of tags.
   * @type {Tag[] | undefined}
   */
  tags?: Tag[];

}

/**
 * The type of a block object.
 * @type {IDObject}
 * @type {T extends object}
 */
export type BlockObject<T extends object> =
  & IDObject
  & T
;


/**
 * The interface of object with blocks.
 * @param {BlockObject[]} blocks
 */
export interface BlocksObject<T extends object> {

  blocks: BlockObject<T>[];

}

/**
 * The interface of an author.
 * @param {UserObject['id']} authorId
 */
export interface AuthorObject {

  /**
   * Id of the author.
   * @type {UserObject['id']}
   */
  authorId: UserObject['id'];

}

/**
 * The type of a toggle.
 * @type {boolean}
 */
export type Toggle = boolean;

/**
 * The interface of an status.
 * @param {T} status
 */
export interface StatusObject<T> {

  /**
   * Status of the instance.
   * @return {T}
   */
  status: T;

}

/**
 * The interface of an energy.
 * @param {number} fats
 * @param {number} proteins
 * @param {number} ch
 * @param {number} kcal
 */
export interface EnergyObject {

  /**
   * Fats of the energy.
   * @type {number}
   */
  fats: number;

  /**
   * Proteins of the energy.
   * @type {number}
   */
  proteins: number;

  /**
   * Ch of the energy.
   * @type {number}
   */
  ch: number;

  /**
   * Kcal of the energy.
   * @type {number}
   */
  kcal: number;

}
